import { toast } from "sonner";
import { format, isValid, parse } from "date-fns";
import axiosInstance from "../../utility/axios";
import {
  clearTraining,
  createTraining,
  deleteTraining,
  disableTrainingLoading,
  editTraining,
  setLoadingTrainingId,
  setMonthTotal,
  setTotalTraining,
  setTraining,
  setTrainingLoading,
  setTrainings,
} from "../../store/slices/training";
import { AppDispatch } from "../../store/store";
import {
  IGetTrainingsResponse,
  IMutateTraining,
  IMutateTrainingResponse,
  ITrainingCount,
  ITrainingForm,
} from "../../store/types/training";
import { getErrorMessage } from "../apiError";

const trainingUrl = "/training/api/v1/admin/trainings";

const formatTrainingDate = (value: string): string => {
  const referenceDate = new Date();
  const parsedDate = [
    parse(value, "yyyy-MM-dd hh:mm aa", referenceDate),
    parse(value, "yyyy-MM-dd hh:mm:ss", referenceDate),
    parse(value, "yyyy-MM-dd HH:mm:ss", referenceDate),
  ].find(isValid);
  const date = parsedDate ?? new Date(value);

  if (!value || !isValid(date)) {
    throw new Error("Enter a valid training date");
  }

  return format(date, "yyyy-MM-dd HH:mm:ss");
};

const toMutationPayload = (formData: ITrainingForm): IMutateTraining => {
  const countryId = Number(formData.country_id);
  const stateId = Number(formData.state_id);
  const date = formatTrainingDate(formData.date);

  if (
    !formData.country_id.trim() ||
    !formData.state_id.trim() ||
    !Number.isInteger(countryId) ||
    countryId <= 0 ||
    !Number.isInteger(stateId) ||
    stateId <= 0
  ) {
    throw new Error("Select a valid country and state");
  }
  return {
    name: formData.name,
    body: formData.body,
    address: formData.address,
    image: formData.image,
    amount: formData.amount,
    duration: formData.duration,
    link: formData.link,
    country_id: countryId,
    state_id: stateId,
    date,
  };
};

const assertSuccess = (success: boolean, message: string, fallback: string) => {
  if (!success) {
    throw new Error(message || fallback);
  }
};

export const get_trainings = async (
  dispatch: AppDispatch,
  offset = 0,
  limit = 10,
): Promise<void> => {
  dispatch(setTrainingLoading());
  try {
    const response = await axiosInstance.get<IGetTrainingsResponse>(
      trainingUrl,
      { params: { offset, limit } },
    );
    assertSuccess(
      response.data.success,
      response.data.message,
      "Failed to load trainings",
    );
    dispatch(setTrainings(response.data));
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to load trainings"));
  } finally {
    dispatch(disableTrainingLoading());
  }
};

export const get_training = async (
  dispatch: AppDispatch,
  id: string,
): Promise<boolean> => {
  dispatch(setTrainingLoading());
  try {
    const response = await axiosInstance.get<IMutateTrainingResponse>(
      `${trainingUrl}/${id}`,
    );
    assertSuccess(
      response.data.success,
      response.data.message,
      "Failed to load training",
    );
    dispatch(setTraining(response.data.data));
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to load training details"));
    return false;
  } finally {
    dispatch(disableTrainingLoading());
  }
};

export const clear_training = (dispatch: AppDispatch) => {
  dispatch(clearTraining());
};

export const create_training = async (
  formData: ITrainingForm,
  dispatch: AppDispatch,
): Promise<boolean> => {
  dispatch(setTrainingLoading());
  try {
    const response = await axiosInstance.post<IMutateTrainingResponse>(
      trainingUrl,
      toMutationPayload(formData),
    );
    assertSuccess(
      response.data.success,
      response.data.message,
      "Failed to create training",
    );
    dispatch(createTraining(response.data.data));
    toast.success(response.data.message || "Training created successfully");
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to create training"));
    return false;
  } finally {
    dispatch(disableTrainingLoading());
  }
};

export const edit = async (
  formData: ITrainingForm & { id: string },
  dispatch: AppDispatch,
): Promise<boolean> => {
  dispatch(setTrainingLoading());
  try {
    const trainingId = formData.id.trim();
    if (!trainingId) {
      throw new Error("A valid training ID is required to update training");
    }

    const response = await axiosInstance.patch<IMutateTrainingResponse>(
      trainingUrl,
      {
        ...toMutationPayload(formData),
        id: trainingId,
      },
    );
    assertSuccess(
      response.data.success,
      response.data.message,
      "Failed to update training",
    );
    dispatch(editTraining(response.data.data));
    toast.success(response.data.message || "Training updated successfully");
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to update training"));
    return false;
  } finally {
    dispatch(disableTrainingLoading());
  }
};

export const delete_training = async (
  dispatch: AppDispatch,
  id: string,
): Promise<boolean> => {
  dispatch(setLoadingTrainingId(id));
  try {
    const response = await axiosInstance.delete<{
      success: boolean;
      message: string;
    }>(`${trainingUrl}/${id}`);
    assertSuccess(
      response.data.success,
      response.data.message,
      "Failed to delete training",
    );
    dispatch(deleteTraining(id));
    toast.success(response.data.message || "Training deleted successfully");
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to delete training"));
    return false;
  } finally {
    dispatch(setLoadingTrainingId(null));
  }
};

export const get_total_training = async (
  dispatch: AppDispatch,
): Promise<void> => {
  dispatch(setTrainingLoading());
  try {
    const response = await axiosInstance.get<ITrainingCount>(
      `${trainingUrl}/analytics`,
    );
    assertSuccess(
      response.data.success,
      response.data.message,
      "Failed to load training total",
    );
    dispatch(setTotalTraining(response.data.data));
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to load training total"));
  } finally {
    dispatch(disableTrainingLoading());
  }
};

export const monthly_total = async (dispatch: AppDispatch): Promise<void> => {
  dispatch(setTrainingLoading());
  try {
    const response = await axiosInstance.get<{ data: number }>(
      "/v1/get-total-month",
    );
    dispatch(setMonthTotal(response.data.data));
  } catch (error) {
    toast.error(
      getErrorMessage(error, "Failed to load monthly training total"),
    );
  } finally {
    dispatch(disableTrainingLoading());
  }
};
